import { ToggleButton } from "@mui/material";
import { useMemo } from "react";

import { SUPPORTED_MEDIUMS } from "./constants";
import { StyledToggleButtonGroup } from "./StyledToggleButtonGroup";
import type { MediumPickerProps, SupportedMedium } from "./types";

import {
  EmailOutlinedIcon,
  LaptopIcon,
  SmartphoneIcon,
  SmsOutlinedIcon,
} from "@/icons";

/**
 * Medium selection toggle buttons
 */
export function MediumPicker({
  frame,
  selectedMedium,
  onMediumSelect,
  variant = "default",
  mode = "medium",
  disabled = false,
}: MediumPickerProps) {
  // Filter to only show supported variants (mms counts toward the "sms" bucket)
  const hasSupportedVariant = useMemo(() => {
    const direct = frame.frame_variants.some((v) =>
      SUPPORTED_MEDIUMS.includes(v.medium as SupportedMedium),
    );
    if (direct) return true;
    return frame.frame_variants.some((v) => v.medium === "mms");
  }, [frame.frame_variants]);

  if (!hasSupportedVariant) {
    return null;
  }

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (value !== null) {
      onMediumSelect?.(value as SupportedMedium);
    }
  };

  return (
    <StyledToggleButtonGroup
      value={selectedMedium}
      exclusive
      onChange={handleChange}
      aria-label="medium selection"
      size="small"
      variant={variant}
      disabled={disabled}
    >
      {mode === "medium"
        ? [
            <ToggleButton key="email" value="email" aria-label="email">
              <EmailOutlinedIcon fontSize="small" />
            </ToggleButton>,
            <ToggleButton key="sms" value="sms" aria-label="text">
              <SmsOutlinedIcon fontSize="small" />
            </ToggleButton>,
          ]
        : [
            <ToggleButton key="email" value="email" aria-label="email desktop">
              <LaptopIcon fontSize="small" />
            </ToggleButton>,
            <ToggleButton
              key="email-mobile"
              value="email-mobile"
              aria-label="email mobile"
            >
              <SmartphoneIcon fontSize="small" />
            </ToggleButton>,
            <ToggleButton key="sms" value="sms" aria-label="text">
              <SmsOutlinedIcon fontSize="small" />
            </ToggleButton>,
          ]}
    </StyledToggleButtonGroup>
  );
}
